import React from "react";
import { CalendarPlus, Download } from "lucide-react";

const CONTACT_EMAIL = "szasztotheniko.office@gmail.com";
const TZ = "Europe/Budapest";
const MONTHS = ["január", "február", "március", "április", "május", "június", "július", "augusztus", "szeptember", "október", "november", "december"];
const pad = (n) => String(n).padStart(2, "0");

// Local Budapest wall-clock times from the public session fields.
function eventTimes(session) {
  const m = session.date_label.match(/(\d{4})\.\s+(\S+)\s+(\d{1,2})\./);
  const [hh, mm] = session.time.split(":").map(Number);
  const start = new Date(Date.UTC(+m[1], MONTHS.indexOf(m[2]), +m[3], hh, mm));
  const end = new Date(start.getTime() + session.duration_minutes * 60000);
  const fmt = (d) => `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}00`;
  return { start: fmt(start), end: fmt(end) };
}

function eventText(title, session) {
  const where = session.format_label || "Online";
  return {
    where,
    details: `${title}\n${session.date_label}, ${session.time} (${session.duration_minutes} perc)\n${where}\nKapcsolat: ${CONTACT_EMAIL}`,
  };
}

function googleUrl(title, session) {
  const { start, end } = eventTimes(session);
  const { where, details } = eventText(title, session);
  const q = new URLSearchParams({ action: "TEMPLATE", text: title, dates: `${start}/${end}`, ctz: TZ, details, location: where });
  return `https://calendar.google.com/calendar/render?${q.toString()}`;
}

const esc = (t) => t.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");

function downloadIcs(title, session) {
  const { start, end } = eventTimes(session);
  const { where, details } = eventText(title, session);
  const ics = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//szasztotheniko.com//Programok//HU", "CALSCALE:GREGORIAN", "METHOD:PUBLISH",
    "BEGIN:VTIMEZONE", `TZID:${TZ}`,
    "BEGIN:DAYLIGHT", "TZOFFSETFROM:+0100", "TZOFFSETTO:+0200", "TZNAME:CEST", "DTSTART:19700329T020000", "RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=-1SU", "END:DAYLIGHT",
    "BEGIN:STANDARD", "TZOFFSETFROM:+0200", "TZOFFSETTO:+0100", "TZNAME:CET", "DTSTART:19701025T030000", "RRULE:FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU", "END:STANDARD",
    "END:VTIMEZONE",
    "BEGIN:VEVENT", `UID:${session.id}@szasztotheniko.com`, `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, "").slice(0, 15)}Z`,
    `DTSTART;TZID=${TZ}:${start}`, `DTEND;TZID=${TZ}:${end}`,
    `SUMMARY:${esc(title)}`, `DESCRIPTION:${esc(details)}`, `LOCATION:${esc(where)}`,
    "END:VEVENT", "END:VCALENDAR",
  ].join("\r\n");
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar;charset=utf-8" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: "forras-kod-alkalom.ics" });
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const BTN = "inline-flex items-center justify-center gap-2.5 px-6 py-3 border font-body text-[12px] tracking-[0.16em] uppercase text-gold border-[rgba(160,122,40,0.45)] hover:bg-[rgba(201,169,97,0.08)] transition-colors duration-300";

export function AddToCalendar({ title, session }) {
  if (!session?.date_label) return null;
  return (
    <div data-testid="add-to-calendar" className="mt-8">
      <p className="font-body text-[10.5px] md:text-[11px] tracking-[0.3em] uppercase text-muted-warm mb-3">Naptárba mentés</p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <a href={googleUrl(title, session)} target="_blank" rel="noopener noreferrer" data-testid="add-to-google-calendar" className={BTN}>
          <CalendarPlus className="w-4 h-4" strokeWidth={1.5} /> Google Naptár
        </a>
        <button type="button" onClick={() => downloadIcs(title, session)} data-testid="download-ics" className={BTN}>
          <Download className="w-4 h-4" strokeWidth={1.5} /> Naptárfájl (iPhone, Outlook)
        </button>
      </div>
    </div>
  );
}
